import React, { useState, useEffect } from 'react';
import useManageOrder from '../order/useManageOrder';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomerProfiles, selectCustomerProfiles } from '../../redux/features/customerProfileSlice';
import ProfileCombo from '../../sharedComponents/ProfileCombo';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from 'react-toastify';
import { extractUrls } from '../../services/UrlValidationService';

function PurchaseImageList() {
  const dispatch = useDispatch();
  const { getOrdersBySmileNameAndUrls, isLoading, error } = useManageOrder();
  const profiles = useSelector(selectCustomerProfiles);
  const shopName = useSelector((state) => state.shop.name);

  const [selectedSmileName, setSelectedSmileName] = useState('');
  const [urlsText, setUrlsText] = useState('');
  const [orders, setOrders] = useState([]);
  const [resetKey, setResetKey] = useState(0);

  // Fetch customer profiles on component mount
  useEffect(() => {
    dispatch(getCustomerProfiles(shopName));
  }, [dispatch, shopName]);

  // Handle customer contact change
  const handleCustomerContactChange = (smileName) => {
    setSelectedSmileName(smileName);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSmileName.trim()) {
      toast.error('Please select a customer profile');
      return;
    }

    if (!urlsText.trim()) {
      toast.error('Please enter URLs');
      return;
    }

    const urls = extractUrls(urlsText);
    if (urls.length === 0) {
      toast.error('No valid URLs found');
      return;
    }

    try {
      const result = await getOrdersBySmileNameAndUrls({
        smileName: selectedSmileName,
        urls: urls
      });

      if (result && result.success) {
        setOrders(result.data || []);
        toast.success(`Found ${result.count || 0} orders`);
      } else {
        toast.error('Failed to fetch orders');
      }
    } catch (error) {
      toast.error('Error fetching orders');
    }
  };

  // Handle link click
  const handleLinkClick = (index) => {
    setOrders(prevOrders => 
      prevOrders.map((order, i) => 
        i === index ? { ...order, linkClicked: true } : order
      )
    );
  };

  // Reset form
  const handleReset = () => {
    setSelectedSmileName('');
    setUrlsText('');
    setOrders([]);
    setResetKey(prev => prev + 1);
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">รูปยืนยันการซื้อ</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Section - Input Form (1/3) */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-semibold mb-4">Search Orders</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Profile Combo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer Profile
                </label>
                <ProfileCombo
                  key={resetKey}
                  profiles={profiles}
                  onChange={handleCustomerContactChange}
                />
              </div>

              {/* URLs Textarea */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URLs (one per line)
                </label>
                <textarea
                  value={urlsText}
                  onChange={(e) => setUrlsText(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={8}
                  placeholder="https://example.com/item1&#10;https://example.com/item2&#10;https://example.com/item3"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    'Search Orders'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Reset
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Section - Results Table (2/3) */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Search Results</h2>
              {orders.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  Found {orders.length} order(s)
                </p>
              )}
            </div>

            {error && (
              <div className="p-4 bg-red-50 border-l-4 border-red-400">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {orders.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <CircularProgress size={40} />
                    <span className="ml-3">Searching orders...</span>
                  </div>
                ) : (
                  'No orders found. Enter a customer profile and URLs to search.'
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Smile Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        URL
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Image
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Purchase Confirm
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order, index) => (
                      <tr 
                        key={index} 
                        className={`${order.linkClicked ? 'bg-green-100 border-l-4 border-green-500' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(order.orderDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {order.smileNameInput}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <a 
                            href={order.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {order.url}
                          </a>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {order.imageUrl && (
                            <img 
                              src={order.imageUrl} 
                              alt="Product" 
                              className="h-12 w-12 object-cover rounded"
                            />
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {order.purchaseConfirmUrl && (
                            <a 
                              href={order.purchaseConfirmUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              onClick={() => handleLinkClick(index)}
                              className={`font-semibold underline ${
                                order.linkClicked 
                                  ? 'text-green-700 bg-green-50 px-2 py-1 rounded' 
                                  : 'text-green-600 hover:text-green-800'
                              }`}
                            >
                              {order.linkClicked ? '✓ Confirmed' : 'Confirm Purchase'}
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PurchaseImageList;